import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from '@nextui-org/react';

interface ValidationErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: string[];
  validPrefixes: string[];
  title?: string;
  description?: string;
  showFormatGuide?: boolean;
}

export default function ValidationErrorModal({
  isOpen,
  onClose,
  errors,
  validPrefixes,
  title = 'Variable Validation Errors',
  description = 'The following variables have invalid formats:',
  showFormatGuide = true,
}: ValidationErrorModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      classNames={{
        base: 'max-w-md',
        body: 'p-6',
        closeButton: 'hover:bg-gray-100 dark:hover:bg-gray-700',
      }}
      placement="center"
    >
      <ModalContent>
        <ModalHeader className="border-b pb-4">
          <h2 className="text-lg font-semibold text-red-600 flex items-center gap-2">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-red-500"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            {title}
          </h2>
        </ModalHeader>
        <ModalBody className="pt-4">
          <div className="space-y-4">
            {/* Error List */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                {description}
              </h3>
              <ul className="space-y-2">
                {errors.map((error, index) => (
                  <li
                    key={index}
                    className="text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200"
                  >
                    {error}
                  </li>
                ))}
              </ul>
            </div>

            {showFormatGuide && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  Valid Variable Format:
                </h4>
                <div className="space-y-2">
                  <p className="text-sm text-blue-800">
                    Variables must follow the format:{' '}
                    <code className="bg-blue-100 px-2 py-1 rounded text-blue-900 font-mono">
                      validPrefix.{'{variable}'}
                    </code>
                  </p>
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-2">
                      Valid Prefixes:
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {validPrefixes.map((prefix) => (
                        <div key={prefix} className="text-sm">
                          <code className="bg-blue-100 px-2 py-1 rounded text-blue-900 font-mono">
                            {prefix}.example
                          </code>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-sm font-medium text-blue-900 mb-2">
                      Examples:
                    </p>
                    <div className="space-y-1">
                      {validPrefixes.map((prefix) => (
                        <code
                          key={prefix}
                          className="block bg-blue-100 px-2 py-1 rounded text-blue-900 font-mono text-sm"
                        >
                          {prefix}.example
                        </code>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="bordered" onPress={onClose}>
                Cancel
              </Button>
              {/* {onFixVariables && (
                <button
                  onClick={() => {
                    onClose();
                    onFixVariables();
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Fix Variables
                </button>
              )} */}
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
